import React, { useState } from 'react';
import {
  Video,
  Users,
  Radio,
  Calendar,
  ShieldCheck,
  Download,
  Scissors,
  Sparkles,
  ArrowRight,
  Play,
  Laptop,
  CheckCircle2,
  HardDrive
} from 'lucide-react';
import { PwaInstallButton } from '@/components/PwaInstallButton';

interface LandingScreenProps {
  onEnterGuestHub: () => void;
  onEnterStudio?: () => void;
}

export const LandingScreen: React.FC<LandingScreenProps> = ({
  onEnterGuestHub,
  onEnterStudio
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const tabInfo = [
    {
      title: 'Local AI Participant Separation',
      desc: 'Automatic face tracking and bounding crops isolate every speaker into individual 1080p MP4 videos.',
      badge: 'Active AI Core'
    },
    {
      title: 'Clipchamp-Style Video Editor',
      desc: 'Multi-track timeline with instant clip splitting (hotkey S), audio detachment, custom crop frames & export.',
      badge: 'Editor Studio'
    },
    {
      title: 'Group Meeting Room',
      desc: 'Multi-participant online video conference with WebRTC P2P direct mesh encryption and zero server recording lag.',
      badge: 'P2P Mesh Encrypted'
    },
    {
      title: 'Studio Podcast Pro',
      desc: 'High-fidelity podcast recording with external mic selection, dual camera controls, live soundboard and audio filters.',
      badge: 'Broadcast Audio'
    },
    {
      title: 'Podcast & Meeting Scheduler',
      desc: 'Direct Google Calendar OAuth sync, auto-invite link generator, timezone conversion and countdown reminders.',
      badge: 'Google Sync'
    }
  ];

  return (
    <div className="w-full min-h-[100dvh] bg-[#060913] text-slate-100 font-sans overflow-x-hidden selection:bg-indigo-500 selection:text-white">
      {/* Dynamic Background Ambient Gradients */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[45vw] h-[45vw] rounded-full bg-indigo-600/15 blur-[120px]" />
        <div className="absolute top-[15%] right-[-5%] w-[40vw] h-[40vw] rounded-full bg-purple-600/15 blur-[130px]" />
        <div className="absolute bottom-[-10%] left-[25%] w-[50vw] h-[50vw] rounded-full bg-cyan-600/10 blur-[140px]" />
      </div>

      {/* =========================================================================
          TOP NAVBAR
         ========================================================================= */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#060913]/80 border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-indigo-500/30 border border-white/20 bg-slate-900 flex items-center justify-center shrink-0">
              <img src="./app-icon.png" alt="MeetSplit AI Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-base sm:text-lg tracking-tight bg-gradient-to-r from-white via-slate-100 to-purple-300 bg-clip-text text-transparent">
                  MeetSplit AI
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                  v2.5 PRO
                </span>
              </div>
              <p className="text-[11px] text-slate-400 hidden sm:block">Record Once. Split Every Speaker.</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* 1-Click Install Button (PWA) */}
            <PwaInstallButton variant="compact" />

            {/* Direct Enter Guest Hub Button */}
            <button
              onClick={onEnterGuestHub}
              className="flex items-center gap-1.5 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white text-xs font-bold shadow-lg shadow-indigo-600/30 transition-all active:scale-95 cursor-pointer"
            >
              <Users className="w-3.5 h-3.5" />
              <span>Join Meeting Hub</span>
              <ArrowRight className="w-3.5 h-3.5 hidden sm:inline" />
            </button>
          </div>
        </div>
      </header>

      {/* =========================================================================
          HERO SECTION
         ========================================================================= */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16">
        <div className="text-center max-w-4xl mx-auto space-y-5">
          {/* Top Pill Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-xs font-semibold shadow-inner">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400" />
            <span>World's First Local AI Video Separation Studio</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.12] text-white">
            Record Once. Split Every Speaker into{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-purple-300 to-cyan-400 bg-clip-text text-transparent">
              Individual HD Videos
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-sm sm:text-base lg:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed font-normal">
            The all-in-one studio for meetings & podcasts. Automatically detect faces, crop speaker tiles, edit multi-track timelines, and sync with Google Calendar — <strong>with zero cloud video upload</strong>.
          </p>

          {/* Primary Call to Action Buttons */}
          <div className="pt-3 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={onEnterGuestHub}
              className="flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:brightness-110 text-white font-extrabold text-sm sm:text-base shadow-xl shadow-indigo-600/40 transition-all active:scale-95 cursor-pointer"
            >
              <Users className="w-4 h-4" />
              <span>Launch Guest Hub (Join Meeting)</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <PwaInstallButton variant="full" />
          </div>

          {/* Supported Systems */}
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-xs text-slate-400 font-medium">
            <span className="flex items-center gap-1.5">
              <Laptop className="w-3.5 h-3.5 text-indigo-400" />
              <span>Windows 10/11</span>
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Laptop className="w-3.5 h-3.5 text-purple-400" />
              <span>macOS</span>
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-emerald-400" />
              <span>1-Click Chrome/Edge PWA</span>
            </span>
          </div>
        </div>

        {/* =========================================================================
            SOFTWARE MOCKUP WINDOW SHOWCASE
           ========================================================================= */}
        <div className="mt-12 sm:mt-16 max-w-5xl mx-auto rounded-3xl p-2 sm:p-3 bg-gradient-to-b from-indigo-500/30 via-purple-500/20 to-cyan-500/20 border border-white/10 shadow-2xl shadow-indigo-500/20">
          <div className="rounded-2xl bg-[#090e1a] border border-white/[0.08] overflow-hidden">
            {/* Window Titlebar */}
            <div className="h-10 px-4 bg-[#0c1424] border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              </div>
              <div className="text-[11px] font-mono text-slate-400">
                meetsplit-studio // {tabInfo[activeTab].title}
              </div>
              <div className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                {tabInfo[activeTab].badge}
              </div>
            </div>

            {/* Mockup Image Preview */}
            <div className="relative aspect-video max-h-[500px] w-full bg-black overflow-hidden flex items-center justify-center">
              <img
                src="./assets/meetsplit-preview.jpg"
                alt="MeetSplit AI Studio Interface"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Interactive Feature Tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-5 bg-[#080d1a] border-t border-white/[0.08] divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]">
              {[
                { label: 'AI Separation', icon: '🤖', sub: 'Auto-Crop Face Tiles' },
                { label: 'Timeline Editor', icon: '✂️', sub: 'Clipchamp-Style (S)' },
                { label: 'Group Meeting', icon: '👥', sub: 'Direct P2P WebRTC' },
                { label: 'Podcast Studio', icon: '🎙️', sub: 'Soundboard & Dual Cam' },
                { label: 'Google Scheduler', icon: '📅', sub: 'Calendar Sync & Links' },
              ].map((tab, idx) => (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  className={`p-3 sm:p-4 text-center transition-all cursor-pointer ${
                    activeTab === idx
                      ? 'bg-indigo-600/15 border-b-2 border-indigo-500'
                      : 'hover:bg-white/[0.03]'
                  }`}
                >
                  <span className="text-base sm:text-lg block mb-0.5">{tab.icon}</span>
                  <span className="text-xs font-bold block text-white">{tab.label}</span>
                  <span className="text-[10px] text-slate-400 block mt-0.5">{tab.sub}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* =========================================================================
            FEATURE SHOWCASE CARDS
           ========================================================================= */}
        <div className="mt-20">
          <div className="text-center max-w-xl mx-auto mb-12 space-y-2">
            <span className="text-xs font-extrabold uppercase tracking-widest text-indigo-400">
              PRO STUDIO CAPABILITIES
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Everything You Need to Create High-Impact Multi-Cam Content
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 flex items-center justify-center text-xl">
                🎯
              </div>
              <h3 className="text-base font-bold text-white">Local AI Participant Separation</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Separates side-by-side or grid video conferences into individual full-screen MP4 videos with auto-centered face framing.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center text-xl">
                ✂️
              </div>
              <h3 className="text-base font-bold text-white">Clipchamp-Style Video Editor</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Multi-track timeline editor: Split clips at playhead with hotkey (S), detach audio from video, and mute individual speakers.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center text-xl">
                🛡️
              </div>
              <h3 className="text-base font-bold text-white">100% Privacy-First Architecture</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Your video recordings and discussions NEVER touch a cloud server. Everything processes on your local hardware.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 flex items-center justify-center text-xl">
                📅
              </div>
              <h3 className="text-base font-bold text-white">Google Calendar & Scheduler</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Connect Gmail / Google Calendar with OAuth. Schedule interviews, generate unique meeting links, and get countdown alerts.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center text-xl">
                ⚡
              </div>
              <h3 className="text-base font-bold text-white">Direct P2P Mesh WebRTC</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Zero-lag audio & video calling between host and guests. Connect instantly using 6-digit room codes or QR codes.
              </p>
            </div>

            <div className="p-6 rounded-3xl bg-slate-900/70 border border-white/10 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center text-xl">
                🖥️
              </div>
              <h3 className="text-base font-bold text-white">1-Click PWA App</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Installs instantly on Windows 10/11, macOS, and Mobile via Chrome / Edge without downloading heavy .exe setups.
              </p>
            </div>
          </div>
        </div>

        {/* =========================================================================
            BOTTOM CALL TO ACTION
           ========================================================================= */}
        <div className="mt-20 p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-blue-900/40 via-indigo-900/40 to-purple-900/40 border border-indigo-500/30 text-center space-y-4">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white">
            Ready to Join a Meeting or Install the App?
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto">
            Install MeetSplit AI directly to your Desktop or launch the Guest Hub in your browser.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={onEnterGuestHub}
              className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white font-bold text-sm shadow-lg transition-all active:scale-95 cursor-pointer"
            >
              <Users className="w-4 h-4" />
              <span>Open Guest Hub (Group & Podcast)</span>
            </button>
            <PwaInstallButton variant="full" />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.08] py-8 text-center text-xs text-slate-500">
        <p>© 2026 MeetSplit AI Studio. Privacy-First Local Computer Vision.</p>
      </footer>
    </div>
  );
};
